// ******************** LAST EDIT - 4/12/21 ************************************

import FeatureForm = require("esri/widgets/FeatureForm");
import Field = require("esri/layers/support/Field");
import FieldElement = require("esri/form/elements/FieldElement");
import FormTemplate = require("esri/form/FormTemplate");
import GroupElement = require("esri/form/elements/GroupElement");
import CodedValueDomain = require("esri/layers/support/CodedValueDomain");
// @ts-ignore
import { prepareUnixFieldValueForDateComponents } from "esri/widgets/support/dateUtils";
import { subclass } from "esri/core/accessorSupport/decorators";
import { tsx } from "esri/widgets/support/widget";
import { IFormOptions, IFormConstructorProps } from "../../src/util/interfaces";
import { ILevelData } from "../../src/aiim/datasets/Levels";
import type Context from "../../src/context/Context";
import type * as tsUtil from "../../src/util/tsUtil";

// @ts-ignore
import { getErrorMessageForFieldInput } from "esri/widgets/FeatureForm/featureFormUtils";


const CSS = {
  select: "esri-select",
  button: "esri-widget--button",
  calendarIcon: "esri-icon esri-icon-calendar",
  closeIcon: "esri-icon esri-icon-close",
  inputIconInvalid: "esri-feature-form__input-icon--invalid",
  errorIcon: "esri-icon-notice-triangle",
  errorMessage: "esri-feature-form__field-error-message",
  description: "esri-feature-form__description-text",
  input: "esri-input",
  inputField: "esri-feature-form__input",
  inputDisabled: "esri-feature-form__input--disabled",
  inputInvalid: "esri-feature-form__input--invalid",
  label: "esri-feature-form__label"
};

const CSS_INDOORS = {
  calendarGroup: "esri-feature-form--calendar-group",
  calendarButton: "esri-feature-form--calendar-button",
  calendarClearButton: "esri-feature-form--calendar-clear-button",
  dataGroup: "esri-feature-form--calendar-button-data-group",
  dateText: "esri-feature-form--calendar-button-date-text",
  editToggle: "esri-feature-form--edit-toggle",
  readOnlyLabel: "esri-feature-form--read-only"
};

const LEVEL_ID = "level_id";
const HEIGHT = "height_relative";
const dateFormatDisplay = "ll";
const defaultDateFormat = "L";
const emailRegex = new RegExp([
  "(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|\"",
  "(?:[\\x01-\\x08\\x0b\\x0c\\x0e-\\x1f\\x21\\x23-\\x5b\\x5d-\\x7f]|\\\\",
  "[\\x01-\\x09\\x0b\\x0c\\x0e-\\x7f])*\")",
  "@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|",
  "\\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\\.){3}",
  "(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:",
  "(?:[\\x01-\\x08\\x0b\\x0c\\x0e-\\x1f\\x21-\\x5a\\x53-\\x7f]|\\\\",
  "[\\x01-\\x09\\x0b\\x0c\\x0e-\\x7f])+)\\])"].join(""));

/** Validates email using RFC5322 compliant regex (with escaped backslashes).
 * [Read more...](https://stackoverflow.com/a/201378)
 */
const isValidEmail = (email: string) => email != null && email.length
  ? emailRegex.test(email.toLowerCase())
  : true;

@subclass("app.custom.FeatureForm.CustomFeatureForm")
export default class CustomFeatureForm extends FeatureForm {

  indoorsContext: Context = null;
  indoorsOptions: IFormOptions = null;
  tsUtil: typeof tsUtil
  levels: ILevelData[] = [];
  _dynamic = {};

  constructor(params: IFormConstructorProps) {
    super(params);
    this.indoorsContext = params.indoorsContext;
    this.indoorsOptions = params.indoorsOptions;
    this.tsUtil = params.tsUtil;
    this.indoorsInit();

    //this._onTextInputChange = this._onTextInputChange.bind(this);
    this._clearDate = this._clearDate.bind(this);
  }

  private _afterComponentCreateOrUpdate(node): void {
    if (node && typeof node.setFocus === "function") {
      // @ts-ignore
      super._afterComponentCreateOrUpdate(node);
    } else if (node && typeof node.focus === "function") {
      const { viewModel } = this;
      // @ts-ignore
      const input = this._getFieldInputFromHTMLElement(node);
      // @ts-ignore
      const focusedInput = viewModel.findField(this._focusedFieldName);
      // @ts-ignore
      if (!this._attemptFocusOnNextRender || focusedInput !== input) {
        return;
      }
      // @ts-ignore
      this._attemptFocusOnNextRender = false;
      // if (isInputInGroupInput(input)) {
      //   input.group.open = true;
      // }
      node.focus();
    }
  }

  _handleFormKeyDown(event: KeyboardEvent): void {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  }

  private _renderLabeledField(fieldInput: __esri.FieldInput) {
    // @ts-ignore
    if (fieldInput.field.type === "time-only") return super._renderLabeledField(fieldInput);
    const { dataType, label, editable, value } = fieldInput;
    const { feature, layer, indoorsOptions: options } = this;

    const allElements = this.viewModel.formTemplate.elements.map(e => e.type === "field"
      ? e as __esri.FieldElement
      : e.type === "group"
        ? (e as __esri.GroupElement).elements
        : [])
      .flat();
    const element = allElements.find(e => e.fieldName === fieldInput.field.name);
    const toggleCss = element.get("indoorsType") === "edit-toggle" ? CSS_INDOORS.editToggle : "";
    const toggleClicked = () => {
      element.set("indoorsType", "default");
      element.editableExpression = "true";
      fieldInput.set("editable", true);
    }
    // @ts-ignore
    const key = `${layer.id}-${feature.uid}-${fieldInput.name}`;
    const tooltipRefId = `${key}-tooltip`;
    const tooltip: string = element.get("tooltip");
    const input = dataType !== "unsupported"
      ? options.readOnlyDisplayType === "label" && !editable
        ? <span class={this.classes(CSS_INDOORS.readOnlyLabel)}>{value}</span>
        : this._renderFieldInput(fieldInput)
      // @ts-ignore
      : this._renderReadOnlyComponent(fieldInput);
    const labelElement = (
      // @ts-ignore
      <label key={key} id={key} class={this.classes(CSS.label, toggleCss)}>
        {[
          element.get("indoorsType") === "edit-toggle"
            ? <span>{label} 
              <calcite-icon icon="pencil" scale="s" onclick={toggleClicked} title={this.indoorsContext.i18n.general.edit} />
            </span>
            : label,
          <span id={tooltipRefId}>{input}</span>,
          this._renderAuxiliaryText(fieldInput)
        ]}
      </label>
    );
    return ([
      labelElement,
      tooltip.length > 0 &&
      <calcite-tooltip key={key+"--tooltip"} reference-element={tooltipRefId} placement="trailing" label={label} overlay-positioning="fixed">
        {element.get("tooltip")}
      </calcite-tooltip>
    ]);
  }

  private _renderFieldInput(fieldInput: __esri.FieldInput) {
    if (fieldInput.hint === "__dynamicDomain__") {
      return this.indoorsRenderDynamicDomain(fieldInput);
    } else if (fieldInput.hint === "__customDate__") {
      return this.indoorsRenderCustomDate(fieldInput);
    }
    if (!fieldInput.domain && fieldInput.inputType !== "text-area" && fieldInput.dataType === "text") {
      const f = fieldInput.field;
      if (f && f.length > 254) {
        // @ts-ignore
        const props = this.getCommonInputProps(fieldInput);
        return f.name.toLowerCase().includes("email")
          ? <input {...props} type="email" />
          : <textarea {...props} />;
      }
    }
    // @ts-ignore
    return super._renderFieldInput(fieldInput);
  }

  private _renderAuxiliaryText(fieldInput: __esri.FieldInput) {
    if (!this.isValidEmail(fieldInput)) {
      return (
        <calcite-input-message status="invalid" active icon={true}>
          {this.indoorsContext.i18n.messages.invalidEmail}
        </calcite-input-message>
      );
    }
    // @ts-ignore
    return super._renderAuxiliaryText(fieldInput);
  }

  /* Indoors *************************************************************** */

  indoorsInit() {
    let elements: (__esri.FieldElement|__esri.GroupElement)[] = [];
    const options = this.indoorsOptions;
    const layer = options.layer;
    const attributes = options.feature.attributes;
    let top: string[], useAdvancedGroup = false, advancedGroupLabel;

    const addNonNullable = (top: string[]) => {
      layer.fields.forEach(f => {
        let lc = f.name.toLowerCase();
        if (!f.nullable && top.indexOf(lc) === -1) {
          top.push(lc);
        }
      })
    }

    const promote = (top: string[]) => {
      let topFields = [], otherFields = [];
      layer.fields.forEach(f => {
        let lc = f.name.toLowerCase();
        let idx = top.indexOf(lc)
        if (top.indexOf(lc) === -1) {
          otherFields.push(f)
        } else {
          topFields[idx] = f;
        }
      })
      let fields = [];
      topFields.forEach(f => {if (f) fields.push(f)});
      fields = fields.concat(otherFields);
      return fields;
    }
    const demote = (fields: __esri.Field[], bottom: string[]) => {
      const newFields = [...fields];
      if (!Array.isArray(bottom)) {
        return newFields;
      }
      bottom.forEach(f => {
        const idx = newFields.findIndex(field =>
          field.name.toLowerCase() === f.toLowerCase());
        if (idx !== -1) {
          newFields.push(newFields.splice(idx, 1)[0]);
        }
      });
      return newFields;
    }    
    const setNonNullable = (names: string[]) => {
      layer.fields.forEach(f => {
        let lc = f.name.toLowerCase();
        if (names.indexOf(lc) !== -1) {
          f.nullable = false;
        }
      })
    }

    let readOnlyFields = ["unit_id","area_id","level_id","assignment_type"];
    let nonVisibleFields = [];
    if (options.type === "unit") {
      nonVisibleFields = nonVisibleFields.concat([
        "site_id","site_name",
        "facility_id","facility_name",
        "level_id","level_name","level_number","vertical_order",
        "section_id","section_name","height_relative"
      ]);
      if (!options.allowName) {
        nonVisibleFields = nonVisibleFields.concat([
          "name","name_long"
        ]);
      }
    } 
    if (options.type === "person") {
      nonVisibleFields = nonVisibleFields.concat([
        "site_id","site_name",
        "facility_id","facility_name",
        "level_id","level_name","level_number","vertical_order",
        /*"unit_id",*/"unit_name",
        "section_id","section_name",
        "access_type","use_type",
        "capacity","utilization",
        "area_gross","area_net","area_um",
        "elevation_absolute","elevation_relative",
        "height_absolute","height_relative",
        "name_long","name_subtitle","description"
      ]);
    }

    if (options.type === "detail") {
      nonVisibleFields = nonVisibleFields.concat([
        "detail_id","level_id"
      ]);
    }

    let fields = layer.fields;
    if (options.type === "unit" && options.forDrawUnit) {
      readOnlyFields = ["area_id","level_id","assignment_type"];
      setNonNullable(["use_type","name","unit_id"]);
      top = ["use_type","name","name_long","schedule_email","capacity","unit_id"];
      addNonNullable(top);
      fields = promote(top);
      useAdvancedGroup = true;      
    }
    if (options.type === "unit" && options.forSplit) {
      setNonNullable(["use_type","name"]); 
      top = ["use_type","name","name_long","schedule_email","area_gross","capacity"]
      addNonNullable(top);
      fields = promote(top);
      useAdvancedGroup = true;
    }
    if (options.type === "detail" && options.forSplit) {
      setNonNullable(["use_type"]);
      top = ["use_type","height_relative"];
      addNonNullable(top);
      fields = promote(top);
      useAdvancedGroup = true;
    }
    if (options.occupantType === "placeholder") {
      setNonNullable(["knownas"]); 
      top = ["knownas"]
      nonVisibleFields.push("email")
      useAdvancedGroup = true;
      advancedGroupLabel = this.indoorsContext.i18n.spaceplanner.people.add.more;
    }
    if (options.isPlaceholder) {
      nonVisibleFields.push("email")
    }

    // check for external configuration of fields
    if (options.readOnlyFields != null
      && options.nonVisibleFields != null
      && options.topFields != null
      && options.nonNullableFields != null
      && options.useGroup != null
    ) {
      readOnlyFields = [...options.readOnlyFields];
      nonVisibleFields = [...options.nonVisibleFields];
      setNonNullable([...options.nonNullableFields]);
      top = [...options.topFields];
      fields = promote(top);
      fields = demote(fields, options.bottomFields);
      useAdvancedGroup = options.useGroup;
      // check if level id is being edtied
      const levelIdIndex = fields.findIndex(f => f.name.toLowerCase() === LEVEL_ID);
      if (options.type !== "level" && levelIdIndex !== -1 && !nonVisibleFields.includes(LEVEL_ID)) {
        const levelIdField = fields[levelIdIndex];
        const levelNameField = Field.fromJSON(levelIdField.toJSON());
        if (options.showAllLevels !== true) {
          const currentLevelId = attributes[levelIdField.name];
          const currentLevel = this.indoorsContext.aiim.datasets.levels.getLevelData(currentLevelId);
          if (!currentLevel) {
            console.warn(`Unable to find level with an ID = '${currentLevelId}'. Displaying all levels...`);
            this.levels = this.indoorsContext.aiim.datasets.levels.getAllLevels();
          } else {
            const facility = this.indoorsContext.aiim.datasets.levels.getFacilityData(currentLevel.facilityId);
            this.levels = facility.levels;
          }
        } else {
          this.levels = this.indoorsContext.aiim.datasets.levels.getAllLevels();
        }
        levelNameField.alias = this.indoorsContext.i18n.editor.attributes.levelNameAlias;
        levelNameField.domain = new CodedValueDomain({
          codedValues: [...this.levels]
            .sort((a, b) => a.levelName.localeCompare(b.levelName))
            .map(l => ({ name: l.levelName, code: l.levelId }))
        });
        fields[levelIdIndex] = levelNameField;
        this.addHandles([
          this.on("value-change", this.updateHeightValue),
          this.on("submit", this.updateZValue)
        ]);
      }
    } else if (this.indoorsOptions.formTemplate != null) {
      this.viewModel.layer = layer;
      this.formTemplate = this.indoorsOptions.formTemplate;
      return;
    }
    const makeElement = (f: __esri.Field): FieldElement => {
      let lc = f.name.toLowerCase();
      let readOnly = (readOnlyFields.indexOf(lc) !== -1);
      let nonVis = (nonVisibleFields.indexOf(lc) !== -1);
      if (f.editable && !nonVis && !readOnly) {
        const label = f.alias || f.name;
        const tooltip = typeof options.getTooltip === "function" && options.getTooltip(options.type, f.name, label);
        const element = new FieldElement({
          fieldName: f.name,
          label: !f.nullable && options.showAsterisk
            ? this.indoorsContext.i18n.general.requiredField.replace("{field}", label)
            : label,
          editableExpression: f.editable && !readOnly ? "true" : "false"
        });
        element.set("tooltip", tooltip);
        if (f.domain) {
          element.domain = f.domain as (CodedValueDomain | __esri.RangeDomain);
        }
        if ((options.type === "unit" || options.type === "detail") && lc === "use_type" && !f.domain) {
          element.hint = "__dynamicDomain__";
          // if detail and use type - pass values through options
          Promise.resolve().then(()=> {
            if(options.useTypes) {
              return options.useTypes
            } else {
              return this.tsUtil.queryUniqueValues({
                field: f,
                fieldName: f.name,
                where: "1=1",
                source: {
                  url: layer.url + "/" + layer.layerId,
                  layer2D: layer
                }
              })
            }
          }).then(result => {
            this._dynamic[f.name] = {
              selVal: attributes[f.name],
              values: result
            }
            this.scheduleRender();
          }).catch(ex => {
            console.error("Error querying unique values",ex);
          })
        } else if (options.type === "person" && lc === "start_date") {
          if (f.type === "date") element.hint = "__customDate__";
        }
        return element;
      }      
    }

    fields.forEach(f => {
      let element = makeElement(f)
      if (element) elements.push(element);
    })

    if (useAdvancedGroup && top && top.length > 0 && elements && elements.length > 0) {
      let primary: FieldElement[] = [], secondary: FieldElement[] = [];
      elements.forEach(el => {
        if (el && el.type === "field" && el.fieldName) {
          let lc = el.fieldName.toLowerCase();
          if (top.indexOf(lc) !== -1) {
            primary.push(el);
          } else {
            secondary.push(el);
          }
        }
      });
      if (primary && primary.length > 0 && primary.length < 10) {
        if (secondary.length < 3 && options.type !== "level") {
          primary.push(...secondary); //.forEach(el => primary.push(el));
          secondary = null;
        }
        if (secondary && secondary.length > 0) {
          const groupElement = new GroupElement({
            label: advancedGroupLabel || this.indoorsContext.i18n.editor.moreAttributes,
            initialState: options.expandGroup === true ? "expanded" : "collapsed",
            elements: secondary
          });
          elements = primary;
          elements.push(groupElement);
        } else {
          elements = primary;
        }
      }
    }

    let ft = new FormTemplate({
      elements: elements
    });
    this.viewModel.layer = layer;
    this.formTemplate = ft;
  }

  indoorsRenderDynamicDomain(fieldInput: __esri.FieldInput) {
    fieldInput.set("domain", fieldInput.field.domain);
    const i18n = this.indoorsContext.i18n;
    const dyn = this._dynamic[fieldInput.field.name];
    const values = dyn && dyn.values;
    const showAddVal = dyn && dyn.showAddVal;
    const k = fieldInput.field.name;
    if (!values) {
      return (<div key={k+"_w"} class="i-waiting-line">...</div>);
    }

    // @ts-ignore
    //const value = this.viewModel.getValue(inputField.name);
    // @ts-ignore
    const props = this.getCommonInputProps(fieldInput);

    let value = props.value;
    if (value && values && typeof value === "string") {
      let lc = value.toLowerCase();
      let hasExactMatch = false, candidate;
      values.some(info => {
        let val = info.value;
        if (value === val) {
          hasExactMatch = true;
          return true;
        } else if (!candidate) {
          if (typeof val === "string" && lc === val.toLowerCase()) {
            candidate = val;
          }
        }
        return false;
      });
      if (!hasExactMatch && candidate) {
        value = candidate;
      }
    }

    const addClicked = () => {
      if (dyn) {
        dyn.showAddVal = true;
        dyn.isMissingValue = true;
      }
      this.scheduleRender();
    }

    const cancelAddClicked = () => {
      if (dyn) {
        let v = dyn.selVal;
        this.viewModel.setValue(fieldInput.name, v);
        // @ts-ignore
        this._inputsWithChanges.add(fieldInput.name);
        dyn.showAddVal = false;
        this.scheduleRender();
      }
    }

    const selChange = event => {
      if (dyn) {
        dyn.selVal = event.target.value;
        // @ts-ignore
        this._saveValueFromComponent(event.target);
      }
    }

    const valChange = event => {
      if (dyn) {
        let v = event.target.value;
        if (typeof v === "string") {
          v = v.trim();
          if (v.length === 0) {
            v = dyn.selVal;
            dyn.isMissingValue = true;
          } else {
            dyn.isMissingValue = false;
            if (values) {
              values.some(info => {
                let val = info.value;
                if (typeof val === "string") {
                  if (v.toLowerCase() === val.toLowerCase()) {
                    v = val;
                    return true;
                  }
                }
                return false;
              });
            }
          }
        }
        this.viewModel.setValue(fieldInput.name, v);
        // @ts-ignore
        this._inputsWithChanges.add(fieldInput.name);
        // this.scheduleRender();
      }
    };

    const addCss = `esri-widget--button${this.disabled ? " esri-disabled" : ""}`
    if (showAddVal && !this.disabled) {
      let {maxLength, minLength} = fieldInput;
      const p = {
        "minlength": minLength > -1 ? `${minLength}` : "",
        "maxlength": maxLength > -1 ? `${maxLength}` : "",
        //"value": value == null ? "" : `${value}`,
      }
      return (
        <div>
          <div key={k+"_txt"} class="i-flex-between">
            <input type="text" class="esri-input esri-feature-form__input"
              {...p}
              tabindex="0" oninput={valChange}
            />
            <button type="button"
              class={addCss}
              title={i18n.general.cancel}
              onclick={cancelAddClicked}>
              <span class="esri-icon esri-icon-close"></span>
            </button>
          </div>
          {dyn.isMissingValue &&
            <calcite-input-message status="invalid" active icon={true}>
              {
                // @ts-ignore
                this.messages.validationErrors.cannotBeNull
              }
            </calcite-input-message>}
        </div>
      );
    }

    const options = [];
    if (fieldInput.field.nullable) {
      // @ts-ignore
      const lbl = this.messages.empty;
      options.push(
        <option key={"empty-option"} value={""}>{lbl}</option>
      );
    }
    values.forEach(info => {
      options.push(
        <option key={info.value} value={info.value}>{info.name}</option>
      )
    });

    const hideUseTypeAdd = this.indoorsContext.isFPE();
    return (
      <div class="i-flex-between">
        <select key={k+"_sel"}
          {...props}
          value={value}
          class={this.classes(props.class, CSS.select)}
          onchange={selChange}
        >{options}</select>
        {!hideUseTypeAdd && <button type="button"
          disabled={this.disabled}
          class={addCss}
          onclick={addClicked}
          title={!this.disabled ? i18n.general.add : ""}>
          <span class="esri-icon esri-icon-plus"></span>
        </button>}
      </div>
    );

  }

  indoorsRenderCustomDate(fieldInput: __esri.FieldInput) {
    const moment = this.tsUtil.moment; // issue #4300
    const locale = this.indoorsContext.lib.dojo.kernel.locale;
    let formattedDate;
    //moment.locale(locale); // issue #4300
    const fieldName = fieldInput.name;
    //@ts-ignore
    let value = this.viewModel.getValue(fieldName);
    if (typeof value !== "number") value = null;

    const { date } = this._formatValueForDateInputs(value);
    if (locale === 'ar') {
      formattedDate = value ? new Date(value) : new Date();
    } else {
      formattedDate = date;
    }
    const displayDate = this._customDateFormat(value);

    const domain = fieldInput.field.domain;
    const maxValue = domain && (domain as __esri.RangeDomain).maxValue;
    const minValue = domain && (domain as __esri.RangeDomain).minValue;
    const maxDate = (maxValue && moment(maxValue)) || null;
    const minDate = (minValue && moment(minValue)) || null;

    const onDateChange = (d) => {
      const parsedDate = moment(d).valueOf();
      this.viewModel.setValue(fieldName, parsedDate);
      // @ts-ignore
      this._inputsWithChanges.add(fieldName);
      this.scheduleRender();
    }

    const calendarClicked = () => {
      //const now = moment();
      //const initialDate = formattedDate ? moment(formattedDate, defaultDateFormat, locale, false) : now;
      const initialDate = value ? new Date(value) : new Date();
      const isOutsideRange = (day) => {
        if (maxDate && minDate) {
          return maxDate.isBefore(day) || minDate.isAfter(day);
        } else if (maxDate) {
          return maxDate.isBefore(day);
        } else if (minDate) {
          return minDate.isAfter(day);
        }
        return false;
      };
      this.tsUtil.showCalendar(initialDate, onDateChange, isOutsideRange);
    }

    const clearButton = (
      <button
        class={this.classes(CSS.button, CSS_INDOORS.calendarClearButton)}
        onclick={() => this._clearDate(fieldInput)}
      >
        <span class={CSS.closeIcon}></span>
      </button>
    )

    return (
      <div class={CSS_INDOORS.calendarGroup}>
        <button
          class={this.classes(CSS.button, CSS_INDOORS.calendarButton)}
          bind={this}
          onclick={calendarClicked}
        >
          <span class={CSS_INDOORS.dataGroup}>
            <span class={CSS.calendarIcon}></span>
            <span class={CSS_INDOORS.dateText}>{displayDate}</span>
          </span>
        </button>
        {fieldInput.field.nullable && clearButton}
      </div>
    );
  }

  hasInvalidEmail() {
    const inputs = this.viewModel.inputs
      .filter(f => f.type === "field" || f.type === "group")
      .map(f => f.type === "group"
        ? f.inputs as __esri.FieldInput[]
        : f as __esri.FieldInput)
      .flat()
      .filter(f => f.name.toLowerCase().includes("email"));
            
    return inputs.some(input => !this.isValidEmail(input));
  }
  
  isValidEmail(fieldInput: __esri.FieldInput) {
    const { value, name} = fieldInput;
    if (!fieldInput.domain && fieldInput.inputType !== "text-area" && fieldInput.dataType === "text") {
      return name.toLowerCase().includes("email")
      ? isValidEmail(value as string)
      : true;
    }
    return true;
  }
  
  private updateHeightValue(e: __esri.FeatureFormValueChangeEvent) {
    if (e.fieldName.toLowerCase() === LEVEL_ID) {
      // get level info
      const level = this.levels.find(l => l.levelId === e.value);
      // update height field, if applicable
      const heightField = this.viewModel.layer.fields.find(f => f.name.toLowerCase() === HEIGHT);
      if (heightField) {
        this.viewModel.setValue(heightField.name, level.z);
      }
    }    
  }

  /** Updates z values of the input geometry to the specified value. */
  private updateZValue() {
    const layer = this.viewModel.layer;
    const { geometry } = this.viewModel.feature;
    const levelField = layer.getField(LEVEL_ID);
    // determine if level changed
    const updated = this.getValues();
    const changed = this.viewModel.feature.attributes[levelField.name] !== updated[levelField.name];
    if (
      changed &&
      geometry &&
      layer &&
      layer.capabilities.data.supportsZ &&
      (this.indoorsOptions.type === "unit" || this.indoorsOptions.type === "detail")
    ) {
      if (!geometry.hasZ) geometry.hasZ = true;
      const level = this.levels.find(l => l.levelId === updated[levelField.name]);
      const prop = geometry.type === "polygon" ? "rings" : "paths";
      const pathsOrRings: number[][][] = geometry[prop];
      const newRings = pathsOrRings.map(ring => {
        ring.forEach(coord => coord[2] = level.z);
        return ring;
      });
      geometry[prop] = newRings;
    }
  }

  _clearDate(fieldInput: __esri.FieldInput) {
    this.viewModel.setValue(fieldInput.name, null);
    //@ts-ignore
    this._inputsWithChanges.add(fieldInput.name);
    this.scheduleRender();
  }

  _customDateFormat(value) {
    const moment = this.tsUtil.moment; // issue #4300
    const date = moment(value);
    const formattedDate = date.format(dateFormatDisplay);
    return formattedDate === "Invalid date" ? "" : formattedDate;
  }

  private _formatValueForDateInputs(value): {
    time: string | null;
    date: Date | null;
  } {
    if (value == null || isNaN(value)) {
      return { date: null, time: null };
    }
    return prepareUnixFieldValueForDateComponents(value, this.timeZone);
  }

}